import { memo, useMemo } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import Money from '#client/components/ListTransaction/components/Money';
import PaymentStatusLabel from '#client/components/PaymentStatusLabel';
import DirectionTypeIcon from '#client/components/TransactionDirectionTypeIcon';
import { DATE_FORMAT } from '#client/constants/date';
import {
  CHECK_DEPOSIT_STATUS,
  MONEY_COLOR,
  PROCESSING_STATUSES,
  TRANSACTION_DIRECTION
} from '#client/constants/transaction';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useModalContext from '#client/hooks/useModalContext';

import CheckDepositDetailModal from './CheckDepositDetailModal';
import { StackStyled } from './CheckDepositRow.styles';

function CheckDepositRow({
  id,
  type,
  status,
  summary,
  amount,
  createdAt,
  estimatedCompletionDate,
  transactionId,
  reason,
  balance,
  isCheckDeposit = true,
  postedDate,
  direction = TRANSACTION_DIRECTION.CREDIT,
  completedDate
}) {
  const { openModal } = useModalContext();

  const trackButtonPressed = useButtonPressedCallback({
    elementName: 'link_account_modal',
    elementText: 'transactions_expanded',
    appPageName: 'active_banking_dashboard'
  });
  const clickHandler = () => {
    // Tracking Details: Tracking added to click on transactions to see details
    trackButtonPressed();

    openModal({
      component: CheckDepositDetailModal,
      amount,
      createdAt,
      type,
      estimatedCompletionDate,
      status,
      balanceAmount: balance,
      reason,
      id,
      summary,
      dateCompleted: completedDate,
      transactionId,
      isCheckDeposit
    });
  };

  const {
    sizes: { isDesktop }
  } = useBreakpoints();

  const createAtText = dayjs(createdAt ?? postedDate).format(DATE_FORMAT.PRIMARY);
  const statusLabelText = PROCESSING_STATUSES.includes(status)
    ? CHECK_DEPOSIT_STATUS.PROCESSING
    : status;

  const moneyColor = useMemo(() => {
    // When check Deposit status = sent or transaction check direction is credit => color green
    if (
      status === CHECK_DEPOSIT_STATUS.SENT ||
      (!isCheckDeposit && direction === TRANSACTION_DIRECTION.CREDIT)
    ) {
      return MONEY_COLOR.GREEN;
    }

    // When transaction check direction is debit => color primary
    if (!isCheckDeposit && direction === TRANSACTION_DIRECTION.DEBIT) {
      return MONEY_COLOR.PRIMARY;
    }

    return MONEY_COLOR.TERTIARY;
  }, [direction, isCheckDeposit, status]);

  return (
    <Stack
      onClick={clickHandler}
      role="listitem"
      sx={{
        cursor: 'pointer',
        padding: '15px 10px',
        '&:hover': {
          boxShadow: '0px 4px 16px 2px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <StackStyled
        gap="24px"
        pr={0}
        width="100%"
      >
        <StackStyled width="85%">
          <Stack
            alignItems="center"
            direction="row"
            spacing={{ xs: '16px', md: '32px' }}
          >
            <Stack>
              <DirectionTypeIcon direction={direction} />
            </Stack>
            <Stack
              alignItems="start"
              direction="column"
              spacing="4px"
            >
              <Text
                size="md"
                weight="medium"
              >
                {summary}
              </Text>
              <Text
                color="tertiary"
                size="xs"
                whiteSpace="nowrap"
              >
                {createAtText}
              </Text>
            </Stack>
          </Stack>
          {isDesktop && isCheckDeposit && <PaymentStatusLabel status={statusLabelText} />}
        </StackStyled>
        <Stack
          alignItems="end"
          direction="column"
        >
          <Money
            amount={amount}
            color={moneyColor}
            direction={direction}
          />
          {!isDesktop && isCheckDeposit && <PaymentStatusLabel status={statusLabelText} />}
        </Stack>
      </StackStyled>
    </Stack>
  );
}

CheckDepositRow.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  status: PropTypes.string,
  summary: PropTypes.string.isRequired,
  amount: PropTypes.string.isRequired,
  balance: PropTypes.string,
  createdAt: PropTypes.string,
  estimatedCompletionDate: PropTypes.string,
  transactionId: PropTypes.string,
  reason: PropTypes.string,
  isCheckDeposit: PropTypes.bool,
  postedDate: PropTypes.string,
  direction: PropTypes.string,
  completedDate: PropTypes.string
};

export default memo(CheckDepositRow);
